import { createSelector } from '@ngrx/store';
import { getDragonflyState } from '../reducers';

export const getCampaignSearchState = createSelector(
  getDragonflyState,
  (state) => state?.campaignSearch,
);

export const getCategories = createSelector(
  getCampaignSearchState,
  (state) => state?.masterData?.categories,
);

export const getSchemas = createSelector(
  getCampaignSearchState,
  (state) => state?.masterData?.schemas,
);

export const getStatusTypes = createSelector(
  getCampaignSearchState,
  (state) => state?.masterData?.statusTypes,
);

export const getChannels = createSelector(
  getCampaignSearchState,
  (state) => state?.masterData?.channels,
);

export const getCountries = createSelector(
  getCampaignSearchState,
  (state) => state?.masterData?.countries,
);

export const getTargetGroups = createSelector(
  getCampaignSearchState,
  (state) =>
    state?.masterData?.targetGroups?.filter(
      (group) => group === 'LOYALTYMEMBER' || group === 'PERSON',
    ) ?? [],
);

export const getSearchPanelExpanded = createSelector(
  getCampaignSearchState,
  (state) => state?.searchPanelExpanded,
);

export const getBasicFiltersPanelExpanded = createSelector(
  getCampaignSearchState,
  (state) => state?.basicFiltersPanelExpanded,
);

export const getAdvancedPanelExpanded = createSelector(
  getCampaignSearchState,
  (state) => state?.advancedFiltersPanelExpanded,
);

export const getName = createSelector(getCampaignSearchState, (state) => state?.campaignName);

export const getCategory = createSelector(
  getCampaignSearchState,
  (state) => state?.campaignCategoryId,
);

export const getNotes = createSelector(getCampaignSearchState, (state) => state?.notes);

export const getDescription = createSelector(getCampaignSearchState, (state) => state?.description);

export const getStatus = createSelector(getCampaignSearchState, (state) => state?.campaignStatus);

export const getTargetGroup = createSelector(
  getCampaignSearchState,
  (state) => state?.campaignSelectionTarget,
);

export const getSchema = createSelector(getCampaignSearchState, (state) => state?.loyaltySchemaId);

export const getChannel = createSelector(getCampaignSearchState, (state) => state?.campaignChannel);

export const getActiveCampaigns = createSelector(
  getCampaignSearchState,
  (state) => state?.deactivated,
);

export const getDeletedCampaigns = createSelector(
  getCampaignSearchState,
  (state) => state?.deleted,
);

export const getMyCampaigns = createSelector(getCampaignSearchState, (state) => state?.user);

export const getActiveFromStartDate = createSelector(
  getCampaignSearchState,
  (state) => new Date(state?.activeDateFromStartDate),
);

export const showActiveFromStartDateShowClearBtn = createSelector(
  getCampaignSearchState,
  (state) => {
    const dateStr = state?.activeDateFromStartDate;
    if (!dateStr) {
      return false;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
      return false;
    }

    return true;
  },
);

export const getActiveFromEndDate = createSelector(
  getCampaignSearchState,
  (state) => new Date(state?.activeDateFromEndDate),
);

export const showActiveFromEndDateShowClearBtn = createSelector(getCampaignSearchState, (state) => {
  const dateStr = state?.activeDateFromEndDate;
  if (!dateStr) {
    return false;
  }

  const date = new Date(dateStr);
  if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
    return false;
  }

  return true;
});

export const getActiveUntilStartDate = createSelector(
  getCampaignSearchState,
  (state) => new Date(state?.activeDateUntilStartDate),
);

export const showActiveUntilStartDateShowClearBtn = createSelector(
  getCampaignSearchState,
  (state) => {
    const dateStr = state?.activeDateUntilStartDate;
    if (!dateStr) {
      return false;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
      return false;
    }

    return true;
  },
);

export const getActiveUntilEndDate = createSelector(
  getCampaignSearchState,
  (state) => new Date(state?.activeDateUntilEndDate),
);

export const showActiveUntilEndDateShowClearBtn = createSelector(
  getCampaignSearchState,
  (state) => {
    const dateStr = state?.activeDateUntilEndDate;
    if (!dateStr) {
      return false;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
      return false;
    }

    return true;
  },
);

export const getDateTypeFrom = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeFrom,
);

export const getUseTimeFrom = createSelector(getCampaignSearchState, (state) => state?.useTimeFrom);

export const getDateTypeUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeUntil,
);

export const getUseTimeUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.useTimeUntil,
);

export const getShowSingleDateRegularFrom = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeFrom === 'single' && !state?.useTimeFrom,
);

export const getShowRangeDateRegularFrom = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeFrom === 'range' && !state?.useTimeFrom,
);

export const getShowSingleDateTimedFrom = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeFrom === 'single' && state?.useTimeFrom,
);

export const getShowRangeDateTimedFrom = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeFrom === 'range' && state?.useTimeFrom,
);

export const getShowSingleDateRegularUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeUntil === 'single' && !state?.useTimeUntil,
);

export const getShowRangeDateRegularUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeUntil === 'range' && !state?.useTimeUntil,
);

export const getShowSingleDateTimedUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeUntil === 'single' && state?.useTimeUntil,
);

export const getShowRangeDateTimedUntil = createSelector(
  getCampaignSearchState,
  (state) => state?.dateTypeUntil === 'range' && state?.useTimeUntil,
);

export const getSelectedCampaign = createSelector(
  getCampaignSearchState,
  (state) => state?.selectedCampaign,
);

export const getVerificationInput = createSelector(
  getCampaignSearchState,
  (state) => state?.verificationInput,
);

export const getVerificationMismatch = createSelector(
  getCampaignSearchState,
  (state) => state?.verificationMismatch,
);

export const getCampaignNotes = createSelector(
  getCampaignSearchState,
  (state) => state?.campaignNotes,
);

export const getDuplicateCampaignName = createSelector(
  getCampaignSearchState,
  (state) => state?.duplicateCampaignName,
);

export const getOriginalCampaignDeactivation = createSelector(
  getCampaignSearchState,
  (state) => state?.originalCampaignDeactivation,
);

export const getDuplicateCampaignNameMaxLengthExceeded = createSelector(
  getCampaignSearchState,
  (state) => state?.duplicateCampaignNameMaxLengthExceeded,
);

export const getShowDuplicateNameErrorMessage = createSelector(getDuplicateCampaignName, (name) => {
  return name ? false : true;
});

export const getDisabledDuplicationButton = createSelector(
  getDuplicateCampaignName,
  getDuplicateCampaignNameMaxLengthExceeded,
  (name, limitExceeded) => !name || limitExceeded,
);

export const getCampaignDeactivation = createSelector(
  getCampaignSearchState,
  (state) => state?.campaignDeactivation,
);
